/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import {
  Alert,
  Drawer,
  Flex,
  Image,
  Spin,
  Typography,
  theme,
  Divider,
  Row,
  Col,
  Button,
} from "@duxton-web/ui"
import React, { memo, useEffect, useMemo } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation, useParams } from "react-router"
import { actionCreator } from "store/duck/incidentsDrawer"
import { AppState } from "store/store.type"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import dayjs from "dayjs"
import { EventCategory, EventAction } from "constants/tracking"
import { UNSET_TIME } from "constants/date"
import { TroyWebTracker } from "utils/tracking"
import {
  getAmountWithoutCurrencySymbol,
  getAmountWithCurrencySymbol,
} from "utils/currency"
import styles from "./index.modules.css"
import {
  useIncidentAliasContent,
  useIncidentGroupingContent,
} from "./incidentContentMapping"
import IncidentActivityTimeline from "./IncidentAcitivityTimeline"
import { MavConstant } from "./mavConstants"

const { useToken } = theme

function IncidentsDrawer() {
  const { data, isLoading, error } = useSelector(
    (state: AppState) => state.incidentsDrawer.detail,
    shallowEqual,
  )
  const { token } = useToken()
  const textMap = useTranslationsMapTyped({
    disclaimer: "incidents.drawer.disclaimer",
    error: "common.error",
    incidentsDrawerTitle: "incidents.drawer.title",
    reported: "incidents.drawer.reported",
    resolved: "incidents.drawer.resolved",
    activityTitle: "incidents.drawer.activity.title",
    affectedItemsHeading: "incidents.detail.heading.affectedItems",
    subtotal: "incidents.drawer.subtotal",
    appealButton: "incidents.drawer.appealButton",
    appealOnText: "incidents.drawer.appealOnText",
    appealStatusPeriod: "incidents.drawer.appealStatusPeriod",
  })

  const dispatch = useDispatch()
  const { search } = useLocation()
  const history = useHistory()

  const params = new URLSearchParams(search)
  // note: merchantID is not required to retrieve incident from food-cashier, but we need to pass it in for food-max-api's legacy middleware validation.
  const merchantID = params.get("merchantID")

  const { fragmentID: incidentID } = useParams<{ fragmentID?: string }>()

  const incidentGroupingContent = useIncidentGroupingContent(
    data?.incident.incidentGrouping,
  )
  const incidentAliasContent = useIncidentAliasContent(
    data?.incident.incidentAlias,
  )

  useEffect(() => {
    if (merchantID && incidentID) {
      dispatch(
        actionCreator.getIncident({
          incidentID,
          merchantID,
        }),
      )

      TroyWebTracker.sendEvent({
        category: EventCategory.incidentDetail,
        action: EventAction.loadPage,
        labels: {
          MERCHANT_ID: merchantID,
          INCIDENT_ID: incidentID,
        },
      })
    }
  }, [dispatch, incidentID, merchantID])

  const { incidentCreatedAt, incidentClosedAt } = data?.incident ?? {}

  const replacedTranslationStrings = useMemo(() => {
    const createdAtDjs = dayjs(incidentCreatedAt)
    const resolvedAtDjs = dayjs(incidentClosedAt)
    return {
      reported: textMap.reported
        .replace("<date.short>", createdAtDjs.format("MMM D"))
        .replace("<time.device>", createdAtDjs.format("HH:mm")),
      resolved: textMap.resolved
        .replace("<date.short>", resolvedAtDjs.format("MMM D"))
        .replace("<time.device>", resolvedAtDjs.format("HH:mm")),
    }
  }, [textMap, incidentCreatedAt, incidentClosedAt])

  const incidentMetadata = data?.incident?.metadata
  const parsedMetadata = incidentMetadata ? JSON.parse(incidentMetadata) : null
  const appealMeta = parsedMetadata?.appealMeta ?? {}
  const appealLink = appealMeta?.appealLink ?? ""
  const appealedTime = data?.incident?.appealSentTime ?? ""
  const appealStatus = data?.incident?.appealStatus ?? 0
  const appealable = data?.activity?.appealable
  const appealWindow = data?.activity?.appealWindow ?? 0
  const createdAt = data?.incident?.createdAt ?? ""

  const hasValidAppealDate =
    !!appealedTime &&
    appealedTime !== UNSET_TIME &&
    appealStatus !== MavConstant.appealStatusNoEvent

  const appealedTimeContent = hasValidAppealDate
    ? textMap.appealOnText.replace(
        "<date.short.noyear>",
        dayjs(appealedTime).format("DD MMM"),
      )
    : ""

  const affectedItems = data?.incident?.incidentAffectItems ?? []

  const affectedItemsSubtotal = useMemo(() => {
    if (!affectedItems.length) return null
    return getAmountWithCurrencySymbol(
      affectedItems.reduce((total: number, curr: any) => {
        const modifierTotal =
          curr.modifiers?.reduce((modTotal: number, modifier: any) => {
            return modTotal + modifier.quantity * modifier.priceInMajorUnit
          }, 0) ?? 0

        return total + curr.quantity * curr.priceInMajorUnit + modifierTotal
      }, 0),
    )
  }, [affectedItems])

  const renderAffectedItems = () =>
    affectedItems.length > 0 && (
      <Flex vertical>
        {TroyWebTracker.sendEvent({
          category: EventCategory.incidentDetail,
          action: EventAction.showAffectedItems,
          labels: {
            MERCHANT_ID: merchantID,
            INCIDENT_ID: incidentID,
            ITEMS: affectedItems.map((item: any) => item.name).join(","),
            MODIFIERS: affectedItems
              .map(
                (item: any) =>
                  `${item.name} - ${(item.modifiers ?? [])
                    .map((modifier: any) => modifier.name)
                    .join(",")}`,
              )
              .join(","),
          },
        })}
        <Typography.Text className={styles.affectedItemsHeading}>
          {textMap.affectedItemsHeading}
        </Typography.Text>
        {affectedItems.map((item: any) => (
          <Flex vertical className={styles.marginLeft8}>
            <Row>
              <Col span={2} className={styles.textLeft}>
                <Typography.Text className={styles.itemQuantity}>
                  x{item.quantity}
                </Typography.Text>
              </Col>
              <Col span={14}>
                <Typography.Text className={styles.bold}>
                  {item.name}
                </Typography.Text>
              </Col>
              {item.priceInMajorUnit > 0 && (
                <Col span={8} className={styles.textRight}>
                  <Typography.Text className={styles.itemTotalReducedPrice}>
                    {getAmountWithoutCurrencySymbol(
                      item.quantity * item.priceInMajorUnit,
                    )}
                  </Typography.Text>
                </Col>
              )}
            </Row>
            {(item.modifiers ?? []).map((modifier: any) => (
              <Row className={styles.divider}>
                <Col span={2} />
                <Col span={14}>
                  {modifier.quantity > 1 && (
                    <Typography.Text
                      strong
                    >{`[${modifier.quantity}] `}</Typography.Text>
                  )}
                  <Typography.Text>{`${modifier.name}`}</Typography.Text>
                </Col>
                {modifier.priceInMajorUnit > 0 && (
                  <Col span={8} className={styles.textRight}>
                    <Typography.Text className={styles.itemTotalReducedPrice}>
                      {getAmountWithoutCurrencySymbol(
                        modifier.quantity * modifier.priceInMajorUnit,
                      )}
                    </Typography.Text>
                  </Col>
                )}
              </Row>
            ))}
            <Divider className={styles.divider16} />
          </Flex>
        ))}
        <Row className={styles.marginLeft8}>
          <Col span={16}>
            <Typography.Text className={styles.subtotal}>
              {textMap.subtotal}
            </Typography.Text>
          </Col>
          <Col span={8} className={styles.textRight}>
            <Typography.Text className={styles.subtotal}>
              {affectedItemsSubtotal}
            </Typography.Text>
          </Col>
        </Row>
      </Flex>
    )

  return (
    <Drawer
      width={450}
      open={!!incidentID && !!merchantID}
      onClose={() => {
        history.push(`/feedback/incidents?${params.toString()}`)
      }}
      title={textMap.incidentsDrawerTitle}
    >
      <div className={styles.drawerContent}>
        {isLoading && <Spin />}
        {error && (
          <Typography.Text type="danger">{textMap.error}</Typography.Text>
        )}
        {data && (
          <>
            <Flex id="incident-info" vertical>
              <Typography.Title level={4} style={{ marginBottom: 8 }}>
                {data.incident.shortOrderNumber}
              </Typography.Title>
              <Typography.Text style={{ color: token.colorNeutral }}>
                {replacedTranslationStrings.reported}
              </Typography.Text>
              <Typography.Text style={{ color: token.colorNeutral }}>
                {replacedTranslationStrings.resolved}
              </Typography.Text>
            </Flex>
            <Divider className={styles.divider} />
            <Typography.Text className={styles.incidentGrouping}>
              {incidentGroupingContent}
            </Typography.Text>
            <Typography.Text className={styles.incidentAlias}>
              {incidentAliasContent}
            </Typography.Text>
            <Typography.Text>
              &quot;{data.incident.description}&quot;
            </Typography.Text>
            {!!data.incident?.imgURL?.length && (
              <Flex gap="middle" className={styles.incidentImages}>
                {data.incident.imgURL.map(img => (
                  <Flex gap="middle">
                    <Image src={img} width={200} />
                  </Flex>
                ))}
              </Flex>
            )}
            {renderAffectedItems()}
            <Divider className={styles.divider16} />
            <Typography.Title level={5}>
              {textMap.activityTitle}
            </Typography.Title>
            <IncidentActivityTimeline timeline={data.activity.timeline} />
            {/** TODO move this to the bottom - prolly push it with flex or something */}
            <Alert type="neutral" message={textMap.disclaimer} />
            <>
              <span className={styles.drawerFooterSpacing} />
              <div className={styles.drawerFooter}>
                <Button
                  type="primary"
                  block
                  disabled={!appealable}
                  onClick={() => {
                    if (appealLink) {
                      TroyWebTracker.sendEvent({
                        category: EventCategory.incidentDetail,
                        action: EventAction.appealIncident,
                        labels: {
                          MERCHANT_ID: merchantID,
                          INCIDENT_ID: incidentID,
                        },
                      })
                      window.open(appealLink, "_blank")
                    }
                  }}
                >
                  {textMap.appealButton}
                </Button>
                <div className={styles.appealedTime}>
                  {appealable
                    ? textMap.appealStatusPeriod.replace(
                        "<date.short.noyear>",
                        dayjs(createdAt)
                          .add(appealWindow, "days")
                          .format("DD MMM"),
                      )
                    : appealedTimeContent}
                </div>
              </div>
            </>
          </>
        )}
      </div>
    </Drawer>
  )
}

export default memo(IncidentsDrawer)
